"use client";

import { NewrelicEvent } from "@enums/newrelic-events";

const initNewRelicLogging = (() => {
  let isInitialized = false;

  return () => {
    if (typeof window === "undefined" || !window?.newrelic || isInitialized) return;
    isInitialized = true;

    // Store original console methods
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    const logToNewRelic = (level: "Console Error" | "Console Warning", originalMethod: (..._args: any[]) => void, ...args: any[]) => {
      originalMethod(...args);
      if (!window.newrelic) return;
      const firstArg = args[0];
      const errorMessage = firstArg instanceof Error ? firstArg.message : String(firstArg);
      const errorInstance = firstArg instanceof Error ? firstArg : new Error(errorMessage);

      window.newrelic?.noticeError(errorInstance, {
        type: level,
        details: args.slice(1),
        url: window.location.href,
      });
    };

    // Override console.error
    console.error = (...args) => logToNewRelic("Console Error", originalConsoleError, ...args);

    // Override console.warn
    console.warn = (...args) => logToNewRelic("Console Warning", originalConsoleWarn, ...args);
  };
})();

// Function to Ensure Logging is Enabled After New Relic is Loaded
export const setupNewRelicLogging = (() => {
  let isListenerAdded = false;

  return () => {
    if (typeof window === "undefined") return;

    if (document.readyState === "complete") {
      initNewRelicLogging();
    } else if (!isListenerAdded) {
      isListenerAdded = true;
      window.addEventListener("load", initNewRelicLogging, { once: true });
    }
  };
})();

export const waitForNewRelic = (timeout = 10000): Promise<typeof window.newrelic> => {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    (function check() {
      if (window.newrelic) {
        resolve(window.newrelic);
      } else if (Date.now() - start >= timeout) {
        reject(new Error("New Relic not available within timeout"));
      } else {
        requestAnimationFrame(check);
      }
    })();
  });
};

export const recordCustomEvent = (key: NewrelicEvent, value: Object) => {
  if (typeof window === "undefined" || !window?.newrelic) return;
  window.newrelic?.recordCustomEvent(key, value);
};
